import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dono do stream da camera.
 *
 * Fica separado dos leitores (codigo de barras e OCR) porque os dois leem do
 * MESMO video ao mesmo tempo: um procurando codigo, outro esperando o porteiro
 * mandar capturar. Se cada um abrisse a propria camera, o navegador entregaria
 * dois streams do mesmo dispositivo — no celular isso trava o segundo.
 */

export type CameraStatus =
  | 'idle'
  | 'starting'
  | 'live'
  /** Usuario negou a permissao. */
  | 'denied'
  /** Nao ha camera, ou a pagina nao esta em HTTPS. */
  | 'unsupported'
  | 'error';

export interface CameraController {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  message: string | null;
  start: () => void;
  stop: () => void;
  /** Alterna entre traseira e frontal. */
  flip: () => void;
  facing: 'environment' | 'user';
}

/**
 * Congela o frame atual do video num canvas.
 *
 * Mora aqui, junto de quem e dono do video, porque os dois leitores precisam
 * do mesmo servico: o de codigo de barras (fallback ZXing) e o de OCR. O
 * `maxWidth` existe para o barcode — reduzir a imagem acelera muito a busca —
 * enquanto o OCR pede o frame inteiro, porque letra pequena de etiqueta some
 * quando a imagem encolhe.
 */
export function captureFrame(video: HTMLVideoElement, maxWidth?: number): HTMLCanvasElement | null {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return null;

  const scale = maxWidth ? Math.min(1, maxWidth / videoWidth) : 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(videoWidth * scale);
  canvas.height = Math.round(videoHeight * scale);

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Retangulo em fracao da AREA VISIVEL do video (0..1), como a moldura desenha. */
export interface CaptureRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Congela so o pedaco do frame que esta dentro da moldura guia.
 *
 * E o ganho isolado mais alto do OCR de etiqueta, e nao por causa de pixels: o
 * Tesseract segmenta a pagina inteira antes de ler. Entregar o balcao, a mao do
 * porteiro e a caixa ao lado faz ele gastar a segmentacao em regiao que nao
 * interessa e quebrar o bloco do endereco no meio. Recortado, sobra uma coluna
 * de texto — que e o caso em que ele e bom.
 *
 * A conversao nao e direta porque o video e exibido com `object-cover`: o
 * elemento mostra um RECORTE CENTRAL do frame, entao a moldura que o porteiro
 * ve nao corresponde as mesmas coordenadas do frame original. Ignorar isso
 * recortaria a regiao errada — deslocada e com zoom trocado — e o resultado
 * pareceria "OCR ruim" em vez de recorte errado.
 */
export function captureRegion(
  video: HTMLVideoElement,
  region: CaptureRegion,
): HTMLCanvasElement | null {
  const { videoWidth, videoHeight } = video;
  const boxWidth = video.clientWidth;
  const boxHeight = video.clientHeight;
  if (!videoWidth || !videoHeight || !boxWidth || !boxHeight) return null;

  // `object-cover`: a escala e a MAIOR das duas, e o excedente sai pelas bordas.
  const scale = Math.max(boxWidth / videoWidth, boxHeight / videoHeight);
  const shownWidth = videoWidth * scale;
  const shownHeight = videoHeight * scale;
  const offsetX = (shownWidth - boxWidth) / 2;
  const offsetY = (shownHeight - boxHeight) / 2;

  // Da moldura (fracao da caixa) para pixel do frame original.
  const sx = (region.x * boxWidth + offsetX) / scale;
  const sy = (region.y * boxHeight + offsetY) / scale;
  const sw = (region.width * boxWidth) / scale;
  const sh = (region.height * boxHeight) / scale;

  // A moldura pode encostar na borda do frame quando o corte do `object-cover`
  // e agressivo; sem o clamp, `drawImage` recebe origem negativa e devolve
  // faixa preta no lugar do texto.
  const x = Math.max(0, Math.min(sx, videoWidth));
  const y = Math.max(0, Math.min(sy, videoHeight));
  const width = Math.max(1, Math.min(sw, videoWidth - x));
  const height = Math.max(1, Math.min(sh, videoHeight - y));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(video, x, y, width, height, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export function useCamera(): CameraController {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [wanted, setWanted] = useState(false);

  const release = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!wanted) {
      release();
      setStatus('idle');
      return;
    }

    // `getUserMedia` so existe em contexto seguro. Sem esta checagem o erro que
    // chega e um TypeError sobre `mediaDevices` indefinido, que nao ajuda
    // ninguem a descobrir que o problema e o http:// do servidor.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      setMessage(
        'A camera so funciona em HTTPS (ou em localhost). Abra o sistema pelo endereco seguro.',
      );
      return;
    }

    let cancelled = false;
    setStatus('starting');
    setMessage(null);

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: facing },
          // Resolucao alta o suficiente para o OCR ler letra pequena de
          // etiqueta; abaixo disso o nome do destinatario sai ilegivel.
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then(async (stream) => {
        // O efeito pode ter sido descartado enquanto o usuario decidia a
        // permissao — sem isto o stream fica aberto e a luz da camera acesa.
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        release();
        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // Safari rejeita o play() quando a aba perde o foco no meio da
          // abertura; o proximo toque na tela resolve, entao nao e erro fatal.
        }
        if (!cancelled) setStatus('live');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const name = error instanceof DOMException ? error.name : '';

        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setStatus('denied');
          setMessage('Permissao de camera negada. Libere o acesso nas configuracoes do navegador.');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          setStatus('unsupported');
          setMessage('Nenhuma camera encontrada neste dispositivo.');
        } else {
          setStatus('error');
          setMessage('Nao foi possivel abrir a camera.');
        }
      });

    return () => {
      cancelled = true;
      release();
    };
  }, [wanted, facing, release]);

  // Sair da tela com a camera aberta deixaria a luz acesa e o dispositivo
  // ocupado para o proximo app.
  useEffect(() => release, [release]);

  return {
    videoRef,
    status,
    message,
    start: useCallback(() => setWanted(true), []),
    stop: useCallback(() => setWanted(false), []),
    flip: useCallback(() => setFacing((value) => (value === 'environment' ? 'user' : 'environment')), []),
    facing,
  };
}
